import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GetBillingAccountResponseSubscriptionDto, SubscriptionsService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAsOnePlansQueryFn } from 'app/queries/billing/createFetchAsOnePlansQueryFn';
import { useState } from 'react';

import { Content } from './styles/Content';
import { RangeSliderWrapper } from './styles/RangeSliderWrapper';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  subscription: GetBillingAccountResponseSubscriptionDto;
};

export const AdminEditASOneSubscriptionModal = ({ onCancel, onSuccess, subscription }: TProps) => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length;
  const totalCount = subscription?.licenses.length;
  const minLicensesCount = assignedCount > 1 ? assignedCount : 1;
  const [numberOfLicenses, setNumberOfLicenses] = useState(totalCount);

  const plansQuery = useQuery({
    queryKey: billingKeys.allAsOnePlans(),
    queryFn: createFetchAsOnePlansQueryFn(call),
  });
  useNotifyQueryError(plansQuery.error);
  const plan = plansQuery.data?.find((item) => item.id === subscription.productId);
  const price = Number(plan?.price ?? 0);
  const freeSeats = plan?.freeSeats ?? 0;

  const showError = (message: string) => {
    onCancel();
    openModal(ModalNamesEnum.GenericErrorModal, {
      message,
    });
  };

  const updateMutation = useMutation({
    mutationFn: () =>
      call(() =>
        SubscriptionsService.adminUpdate({
          baId: subscription.billingAccountId,
          subscriptionId: subscription.id,
          requestBody: { licenseCount: numberOfLicenses },
        }),
      ),
    onSuccess: (result) => {
      if (!result || !result.success) {
        let message = 'Something went wrong. Please contact administrator.';
        if (result?.error === 'No payment methods found') {
          message = 'Cannot create subscription. Please add the payment method first';
        }
        if (result?.error === 'Subscription already exists') {
          message = 'You already have this subscription. Please choose another or modify an existing subscription';
        }
        if (result?.error === 'Subscription has a failed payment') {
          message = 'This subscription has a failed payment. Please resolve your billing before making changes.';
        }
        showError(message);
        return;
      }
      onSuccess();
    },
    onError: () => {
      showError('Something went wrong. Please contact administrator.');
    },
  });

  const isSubmitting = updateMutation.isPending;

  const innerSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    updateMutation.mutate();
  };

  return (
    <ModalWrapper width="480px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle data-testid="purchasePlan">Confirm</ModalTitle>
      <Content>
        <p className="description">You are about to change the number of licenses for this subscription</p>

        <RangeSliderWrapper>
          <label htmlFor="steps-range" className="block mb-2 font-[600] text-xs text-foreground-muted text-left">
            Number of licenses:
          </label>
          <input
            id="steps-range"
            type="range"
            min={minLicensesCount}
            max={250}
            value={numberOfLicenses}
            step={1}
            onChange={(e) => setNumberOfLicenses(Number(e.target.value))}
            className="w-full h-2 bg-[var(--color-surface-muted)] rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">
            {numberOfLicenses} license{numberOfLicenses > 1 && 's'}
          </span>
        </RangeSliderWrapper>

        <div className="text-left my-4 flex flex-col">
          {freeSeats ? (
            <div>
              <span className="font-[600] text-xs text-foreground-muted">Free licenses in subscription plan:</span>
              <span className="float-right">{freeSeats}</span>
            </div>
          ) : null}
          <div>
            <span className="font-[600] text-xs text-foreground-muted">Total subscription price per month:</span>
            <span className="float-right">
              ${numberOfLicenses <= freeSeats ? 0 : (Number(price) * (numberOfLicenses - freeSeats)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="buttonContainer">
          <OutlinedButton type="button" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <Button data-testid="submitButton" type="submit" onClick={innerSubmit} disabled={isSubmitting}>
            Update
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
